import {
  InstallationOriginType,
  type GroupPermissions,
} from '@wix/platform-editor-sdk';
import { getSelectedComponentAppData } from './utils/handlerUtils';
import {
  TpaHelpService,
  TpaSettingsPanelService,
  createTpaHelpService,
} from '../services';

import type {
  AppData,
  DocumentServicesObject,
} from '@wix/document-services-types';

import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../types/tpaMessage';

export interface EditorTPAPreviewHandlers {
  deselectComponent: TPAHandler;
  onReady: TPAHandler;
  getViewMode: TPAHandler;
  openHelp: TPAHandler;
  openSettingsDialog: TPAHandler;
  editorAddApplication: TPAHandler;
  isCustomApplicationPermissionsGranted: TPAHandler;
  isGroupApplicationPermissionsGranted: TPAHandler;
}

export type TPAPreviewHandlerType = keyof EditorTPAPreviewHandlers;

interface TpaPreviewHandlersServicesDependencies {
  tpaSettingsPanelService: TpaSettingsPanelService;
  tpaHelpService: TpaHelpService;
}

export const createEditorTPAPreviewHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  {
    tpaSettingsPanelService,
    tpaHelpService,
  }: TpaPreviewHandlersServicesDependencies,
): EditorTPAPreviewHandlers => {
  const isCustomApplicationPermissionsGranted = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const customPermissions = msg.data as string[];
    const permissionGranted =
      customPermissions &&
      (await platformApiMethods.document.permissions.isCustomPermissionsGranted(
        {} as AppData,
        '',
        { permissions: customPermissions },
      ));

    callback(permissionGranted);
  };

  const isGroupApplicationPermissionsGranted = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const groupPermissions = msg.data as GroupPermissions[];
    const permissionGranted =
      groupPermissions &&
      (await platformApiMethods.document.permissions.isGroupsPermissionsGranted(
        {} as AppData,
        '',
        { permissions: groupPermissions },
      ));

    callback(permissionGranted);
  };

  const onReady = async (_msg: TPAMessage, callback: TPAMessageCallback) => {
    platformApiMethods.editor.userPreferences.registerInitUserPrefsCallback?.(
      callback,
    );
  };

  const getViewMode = (_msg: TPAMessage, callback: TPAMessageCallback) => {
    const viewMode = documentServicesAPI.documentMode.getComponentViewMode();
    return callback(viewMode);
  };

  const deselectComponent = async () => {
    const selectedComps =
      await platformApiMethods.editor.selection.getSelectedComponents();

    platformApiMethods.editor.selection.deselectComponents({} as AppData, '', {
      compsToDeselect: selectedComps,
    });
  };

  const openHelp = async (msg: TPAMessage) => {
    if (!msg?.data) {
      throw new Error('Message data is required for openHelp handler');
    }

    platformApiMethods.editor.preview.togglePreview?.({
      biParams: { origin: 'tpa' },
    });

    const data = documentServicesAPI.tpa.app.getFirstAppCompPageId(
      String(msg.data),
    );

    if (!data?.compId) {
      throw new Error('Component with given compId is not found');
    }

    const compRef = documentServicesAPI.components.get.byId(
      data.compId,
      data?.pageId,
    );

    if (!compRef) {
      throw new Error('Component with given compId is not found');
    }

    await platformApiMethods.editor.selection.selectComponentByCompRef(
      {} as AppData,
      '',
      { compsToSelect: [compRef] },
    );
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();

    const { appData, origCompData } = await getSelectedComponentAppData(
      platformApiMethods,
      documentServicesAPI,
    );

    const helpId = await tpaHelpService.getHelpId(
      'widget',
      msg?.deviceType,
      selectedComp,
      appData?.appDefinitionId,
      origCompData?.widgetId,
    );

    platformApiMethods.editor.openHelpPanel(appData as AppData, '', {
      helpId,
      origin: 'preview',
      panelName: 'preview',
    });
  };

  const openSettingsDialog = async (
    msg: TPAMessage,
    _callback: TPAMessageCallback,
  ) => {
    const viewMode = documentServicesAPI.documentMode.getComponentViewMode();
    const isPreviewMode = viewMode === 'preview';

    const editorMode = await platformApiMethods.editor.info.getEditorMode();
    const isDesktopEditor = editorMode === 'desktop';

    if (!isPreviewMode || !isDesktopEditor) {
      throw new Error(
        `TPA preview handler openSettingsDialog stopped execution because editor is in ${viewMode} ${editorMode} mode.\n Make sure you are in desktop preview mode`,
      );
    }

    const { pageId, compId } = msg.data || {};

    tpaSettingsPanelService.openSettingsPanel(compId, pageId);
  };

  const editorAddApplication = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const appDefinitionId = msg.data?.appDefinitionId;

    if (!appDefinitionId) {
      throw new Error(
        `"appDefinitionId" should be present in the message data for isApplicationInstalled handler`,
      );
    }

    const isAppInstalled =
      documentServicesAPI.tpa.app.isInstalled(appDefinitionId);

    if (isAppInstalled) {
      callback({
        error: 'Application already installed.',
      });
      throw new Error(`Application ${appDefinitionId} is already installed`);
    }

    const editorMode = await platformApiMethods.editor.info.getEditorMode();
    const isMobileEditor = editorMode === 'mobile';

    if (isMobileEditor) {
      callback({
        error: "Can't add application in mobile editor.",
      });
      throw new Error("Can't add application in mobile editor.");
    }

    const marketData = await documentServicesAPI.tpa.appMarket.getDataAsync(
      appDefinitionId,
    );
    if (!marketData) {
      throw new Error(
        `Application with ${appDefinitionId} appDefinitionId do not exist.`,
      );
    }

    const isWixTPA = marketData.by === 'Wix';

    if (!isWixTPA) {
      if (!marketData?.slug) {
        throw new Error(
          `Application name is missing in appData for appDefinitionId: ${appDefinitionId}.`,
        );
      }

      const appMarketUrl = documentServicesAPI.tpa.appMarket.getPermissionsUrl({
        appName: marketData.slug!,
      });

      const componentRef =
        await platformApiMethods.editor.getCompRefFromCompId?.(msg.compId);

      platformApiMethods.editor.openAppMarketModal(
        { appDefinitionId } as AppData,
        '',
        {
          modalType: 'appMarketPermissionsModal',
          url: appMarketUrl,
          componentRef,
        },
      );
      callback();
    }

    const pageId = msg?.data?.pageId;
    const targetPage = pageId
      ? documentServicesAPI.pages.getReference(pageId)
      : undefined;
    const containerRef = targetPage
      ? (
          await platformApiMethods.editor.sections.getPageSectionsSortedByStageOrder?.(
            targetPage,
          )
        )?.[0]
      : undefined;

    const options = {
      pageId,
      containerRef,
      shouldNavigate: msg?.data?.shouldNavigate ?? false,
      showPageAddedPanel: msg?.data?.showPageAddedPanel ?? false,
      appDefinitionId,
      originInfo: {
        type: InstallationOriginType.IFRAME_JS_SDK,
        containerRef,
      },
      biData: { adding_method: 'sdk' },
    };

    const response = await platformApiMethods.document.tpa.add.application(
      { appDefinitionId } as AppData,
      '',
      options,
    );

    if (response?.instanceId) {
      callback({ instanceId: response.instanceId });
    }
  };

  return {
    deselectComponent,
    onReady,
    getViewMode,
    openHelp,
    openSettingsDialog,
    editorAddApplication,
    isCustomApplicationPermissionsGranted,
    isGroupApplicationPermissionsGranted,
  };
};
